import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../App.css';

function Portfolio() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  
  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);
    
    // Update scroll progress indicator
    const updateScrollProgress = () => {
      const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (winScroll / height) * 100;
      const scrollBar = document.querySelector('.scroll-progress');
      if (scrollBar) {
        scrollBar.style.width = scrolled + '%';
      }
    };

    // Add event listener
    window.addEventListener('scroll', updateScrollProgress);
    
    // Clean up
    return () => window.removeEventListener('scroll', updateScrollProgress);
  }, []);

  const handleContactClick = () => {
    navigate('/contact');
  };

  // Portfolio data
  const projects = [
    {
      id: 1,
      title: 'E-Commerce Platform',
      category: 'web',
      image: '/assets/portfolio/e-commerce.jpg',
      description: 'Complete e-commerce solution with product management, payment integration, and customer analytics. Includes inventory management, multi-vendor support, and comprehensive admin dashboard. Please contact me for more info and demo.',
      technologies: ['Laravel', 'MySQL', 'Bootstrap', 'Payment Gateway', 'Delivery Partner Integration'],
      link: '#'
    },
    {
      id: 2,
      title: 'PropertyMart.Com.My',
      category: 'web',
      image: '/assets/portfolio/pm-logo-bgpink.jpg',
      description: 'PropertyMart.Com.My is a comprehensive property listing platform that allows users to list their properties for sale or rent. Features include advanced search filters, CRM integration, and property management tools.',
      technologies: ['React', 'Laravel', 'MySQL', 'Tailwind CSS', 'CRM'],
      link: 'https://www.propertymart.com.my'
    },
    {
      id: 3,
      title: 'Gold Trading Platform',
      category: 'mobile',
      image: '/assets/portfolio/my-transaction-gold.png',
      description: 'Gold Trading Platform is a secure mobile application that allows users to buy and sell gold with real-time pricing, KYC verification, and integrated payment systems. Please contact me for more info and demo.',
      technologies: ['Flutter', 'Firebase', 'Payment Gateway', 'API Integration', 'KYC'],
      link: '#'
    },
    {
      id: 4,
      title: 'Membership & Loyalty Programme App',
      category: 'mobile',
      image: '/assets/portfolio/e-commerce.jpg',
      description: 'Comprehensive loyalty program mobile application with point accumulation, reward redemption, member tiers, and push notifications. Features gamification elements and analytics dashboard. Please contact me for more info and demo.',
      technologies: ['Flutter', 'Laravel API', 'MySQL', 'Push Notifications', 'Analytics'],
      link: '#'
    }
  ];

  // Filter projects based on current filter
  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(project => project.category === filter);

  return (
    <div className="w-full">
      {/* Scroll Progress Indicator */}
      <div className="scroll-progress"></div>
      
      {/* Hero Section */}
      <header className="bg-white">
        <div className="container">
          <div className="flex justify-between items-center py-8 flex-col-mobile">
            <div>
              <h1 className="mb-4">
                Our <span className="text-flame">Portfolio</span>
              </h1>
              <p className="mb-8 text-gray-600">Showcasing our most impactful digital transformation projects</p>
            </div>
            <div>
              <img src="/assets/portfolio/portfolio.png" alt="Our Portfolio" />
            </div>
          </div>
        </div>
      </header>

      {/* Filter Section */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="filter-controls text-center mb-8">
            <button 
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`} 
              onClick={() => setFilter('all')}
            >
              All Projects
            </button>
            <button 
              className={`filter-btn ${filter === 'web' ? 'active' : ''}`} 
              onClick={() => setFilter('web')}
            >
              Web Applications
            </button>
            <button 
              className={`filter-btn ${filter === 'mobile' ? 'active' : ''}`} 
              onClick={() => setFilter('mobile')}
            >
              Mobile Apps
            </button>
          </div>
          
          <div className="portfolio-grid">
            {filteredProjects.map(project => (
              <div key={project.id} className="portfolio-item">
                <div className="portfolio-image-container">
                  <img src={project.image} alt={project.title} className="portfolio-image" />
                  <div className="portfolio-overlay">
                    <a href={project.link} className="btn view-project-btn">View Project</a>
                  </div>
                </div>
                <div className="portfolio-content">
                  <h3>{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                  <div className="tech-tags">
                    {project.technologies.map((tech, index) => (
                      <span key={index} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section bg-white">
        <div className="container">
          <h2 className="section-title">Our <span className="text-flame">Approach</span></h2>
          <div className="portfolio-process-steps">
            <div className="process-step">
              <div className="process-number">01</div>
              <h3>Discovery</h3>
              <p className="text-gray-600">We start by understanding your business goals, user needs, and technical requirements.</p>
            </div>
            <div className="process-step">
              <div className="process-number">02</div>
              <h3>Strategy</h3>
              <p className="text-gray-600">We create a comprehensive roadmap that aligns with your vision and objectives.</p>
            </div>
            <div className="process-step">
              <div className="process-number">03</div>
              <h3>Design</h3>
              <p className="text-gray-600">Our designers craft intuitive, engaging interfaces that resonate with your audience.</p>
            </div>
            <div className="process-step">
              <div className="process-number">04</div>
              <h3>Development</h3>
              <p className="text-gray-600">We build robust, scalable solutions using the most appropriate technologies.</p>
            </div>
            <div className="process-step">
              <div className="process-number">05</div>
              <h3>Testing</h3>
              <p className="text-gray-600">Rigorous quality assurance ensures your product performs flawlessly.</p>
            </div>
            <div className="process-step">
              <div className="process-number">06</div>
              <h3>Launch & Support</h3>
              <p className="text-gray-600">We ensure a smooth deployment and provide ongoing maintenance and enhancements.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonials */}
      <section className="section bg-gray-50">
        <div className="container">
          <h2 className="section-title">Client <span className="text-flame">Testimonials</span></h2>
          <div className="testimonials-container">
            <div className="testimonial">
              <div className="testimonial-quote">"OnFire transformed our business with an e-commerce platform that increased our sales by 200% in the first year."</div>
              <div className="testimonial-author">
                <div className="testimonial-name">Sarah Johnson</div>
                <div className="testimonial-position">CEO, FashionForward</div>
              </div>
            </div>
            <div className="testimonial">
              <div className="testimonial-quote">"The mobile application developed by OnFire revolutionized how we interact with our customers, resulting in a 45% increase in user engagement."</div>
              <div className="testimonial-author">
                <div className="testimonial-name">Michael Chen</div>
                <div className="testimonial-position">CTO, TechInnovate</div>
              </div>
            </div>
            <div className="testimonial">
              <div className="testimonial-quote">"Their IoT solution for our manufacturing plant reduced operational costs by 30% and improved efficiency across all departments."</div>
              <div className="testimonial-author">
                <div className="testimonial-name">James Rodriguez</div>
                <div className="testimonial-position">Operations Director, IndustrialPro</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="bg-flame text-white py-16 text-center">
        <div className="container">
          <h2 className="mb-4">Ready to Build Your Next Project?</h2>
          <p className="mb-8">Let's create something amazing together that exceeds expectations.</p>
          <button className="btn btn-white" onClick={handleContactClick}>Start a Project</button>
        </div>
      </section>
    </div>
  );
}

export default Portfolio; 